import { getCurrentPage, getDynamicData, isComponentType, useDynamicData } from '@avensia/excite';
import { type CartCommandEventData } from './command';

export function isCheckoutPage() {
  return isComponentType<Excite.Commerce.ICheckoutPageApiModel>(getCurrentPage());
}

export type CartUpdateOptions = {
  resolveCheckoutData?: boolean;
  eventData?: CartCommandEventData;
};

export function useIsCartEmpty(cartId?: string) {
  const [isCartEmpty] = useDynamicData<Excite.Commercetools.CartApiModel, boolean>({
    selector: cart => cart?.items.length === 0,
    shouldUpdate: (prev, next) => prev !== next,
    ...(cartId && { context: { cartId } }),
  });

  return isCartEmpty;
}

export async function addLineItem(
  sku: string,
  quantity = 1,
  { resolveCheckoutData = isCheckoutPage(), eventData }: CartUpdateOptions = {},
  cartId?: string,
  customCartCommand?: string,
) {
  const { send } = await import('./command');

  return send(
    {
      $type: customCartCommand ?? 'AddLineItemCommand',
      sku,
      quantity,
      resolveCheckoutData,
      trackingType: 'addLineItem',
      cartId,
    },
    eventData,
  );
}

export async function addPackage(
  packageKey: string,
  packageParts: Excite.Commercetools.PackagePartRequest[],
  quantity: number = 1,
  { resolveCheckoutData = isCheckoutPage(), eventData }: CartUpdateOptions = {},
  customCartCommand?: string,
) {
  const { send } = await import('./command');

  return send(
    {
      $type: customCartCommand ?? 'AddPackageCommand',
      packageKey,
      packageParts,
      quantity,
      resolveCheckoutData,
      trackingType: 'addLineItem',
    },
    eventData,
  );
}

function optimisticQuantityChange(lineItemId: string, quantity: number, cartId?: string) {
  const cart = getDynamicData<Excite.Commercetools.CartApiModel>(cartId ? { cartId } : undefined);
  cart.update(current => {
    const items: Excite.Commercetools.LineItemApiModel[] = [];
    for (const item of current.items) {
      if (item.id === lineItemId) {
        if (quantity > 0) {
          items.push({ ...item, quantity });
        }
      } else {
        items.push(item);
      }
    }

    return { ...current, items };
  });
}

async function handleOptimisticQuantityChange(
  commandResponse: Promise<Excite.Commercetools.CartApiModel | Excite.Commercetools.CartValidationApiModel>,
  lineItemId: string,
  quantity: number,
  previousQuantity?: number,
  cartId?: string,
) {
  optimisticQuantityChange(lineItemId, quantity, cartId);
  const response = await commandResponse;
  if (previousQuantity !== undefined) {
    const validation = response as Excite.Commercetools.CartValidationApiModel;
    if (validation?.hasErrors) {
      optimisticQuantityChange(lineItemId, previousQuantity, cartId);
    }
  }
  return response;
}

export async function changeLineItemQuantity(
  lineItemId: string,
  quantity: number,
  { resolveCheckoutData = isCheckoutPage(), eventData }: CartUpdateOptions = {},
  cartId?: string,
  customCartCommand?: string,
) {
  // Must run before optimistic update
  const updatedEventData = await updateEventDataWithPreviousData(eventData, lineItemId, cartId);

  const { send } = await import('./command');

  return handleOptimisticQuantityChange(
    send(
      {
        $type: customCartCommand ?? 'ChangeLineItemQuantityCommand',
        lineItemId,
        quantity,
        resolveCheckoutData,
        trackingType: 'changeLineItemQuantity',
        cartId,
      },
      updatedEventData,
    ),
    lineItemId,
    quantity,
    updatedEventData?.previousQuantity,
  );
}

export async function removeLineItem(
  lineItemId: string,
  { resolveCheckoutData = isCheckoutPage(), eventData }: CartUpdateOptions = {},
  cartId?: string,
  customCartCommand?: string,
) {
  // Must run before optimistic update
  const updatedEventData = await updateEventDataWithPreviousData(eventData, lineItemId, cartId);

  const { send } = await import('./command');

  return handleOptimisticQuantityChange(
    send(
      {
        $type: customCartCommand ?? 'ChangeLineItemQuantityCommand',
        lineItemId,
        quantity: 0,
        resolveCheckoutData,
        trackingType: 'removeLineItem',
        cartId,
      },
      updatedEventData,
    ),
    lineItemId,
    0,
    updatedEventData?.previousQuantity,
  );
}

export async function removeCustomLineItem(
  customLineItemId: string,
  { resolveCheckoutData = isCheckoutPage(), eventData }: CartUpdateOptions = {},
  cartId?: string,
  customCartCommand?: string,
) {
  // Must run before optimistic update
  const updatedEventData = await updateEventDataWithPreviousData(eventData, customLineItemId, cartId);

  const { send } = await import('./command');

  return handleOptimisticQuantityChange(
    send(
      {
        $type: customCartCommand ?? 'RemoveCustomLineItemCommand',
        customLineItemId,
        resolveCheckoutData,
        trackingType: 'removeLineItem',
        cartId,
      },
      updatedEventData,
    ),
    customLineItemId,
    0,
    updatedEventData?.previousQuantity,
  );
}
async function updateEventDataWithPreviousData(
  eventData: CartCommandEventData | undefined,
  lineItemId: string,
  cartId?: string,
) {
  if (eventData) {
    const { items } = await getDynamicData<Excite.Commercetools.CartApiModel>(
      cartId ? { cartId } : undefined,
    ).current();
    const item = items.find(item => item.id === lineItemId);

    if (item) {
      const updatedEventData: CartCommandEventData = {
        ...eventData,
        previousQuantity: item.quantity,
        previousLineItem: item,
      };

      return updatedEventData;
    }
  }

  return eventData;
}

/**
 * This can be used instead of addDiscountCode and addGiftCardCode if you have a single code field
 * as it checks the entered code both against discounts and gift cards
 */
export async function addCode(code: string, resolveCheckoutData = true) {
  const { send } = await import('./command');
  return send({
    $type: 'AddCartDiscountOrGiftCardCodeCommand',
    code,
    resolveCheckoutData,
  });
}

/**
 * This can be used instead of removeDiscountCode and removeGiftCardCode if you have a single code field
 * as it checks the entered code both against discounts and gift cards
 */
export async function removeCode(code: string, resolveCheckoutData = true) {
  const { send } = await import('./command');
  return send({
    $type: 'RemoveCartDiscountOrGiftCardCodeCommand',
    code,
    resolveCheckoutData,
  });
}

export async function addDiscountCode(code: string, resolveCheckoutData = true) {
  const { send } = await import('./command');
  return send({
    $type: 'AddDiscountCodeCommand',
    code,
    resolveCheckoutData,
  });
}

export async function removeDiscountCode(code: string, resolveCheckoutData = true) {
  const { send } = await import('./command');
  return send({
    $type: 'RemoveCartDiscountCodeCommand',
    code,
    resolveCheckoutData,
  });
}

export async function addGiftCardCode(code: string, resolveCheckoutData = true) {
  const { send } = await import('./command');
  return send({
    $type: 'AddGiftCardCodeCommand',
    code,
    resolveCheckoutData,
  });
}

export async function removeGiftCardCode(code: string, resolveCheckoutData = true) {
  const { send } = await import('./command');
  return send({
    $type: 'RemoveGiftCardCodeCommand',
    code,
    resolveCheckoutData,
  });
}

export async function addVoucher(
  data: Omit<Excite.Commercetools.AddCustomLineItemVoucherCommand, '$type'>,
  resolveCheckoutData = true,
) {
  const { send } = await import('./command');
  return send({
    $type: 'AddCustomLineItemVoucherCommand',
    ...data,
    resolveCheckoutData,
  });
}

export async function removeVoucher(voucherId: string, resolveCheckoutData = true) {
  const { send } = await import('./command');
  return send({
    $type: 'RemoveCustomLineItemVoucherCommand',
    voucherId,
    resolveCheckoutData,
  });
}

export async function setCustomerEmail(email: string, resolveCheckoutData = true, cartId?: string) {
  const { send } = await import('./command');
  return send({
    $type: 'SetCustomerEmailCommand',
    email,
    resolveCheckoutData,
    cartId,
  });
}

export async function setCustomerMobile(mobile: string, resolveCheckoutData = true, cartId?: string) {
  const { send } = await import('./command');
  return send({
    $type: 'SetCustomerMobileCommand',
    mobile,
    resolveCheckoutData,
    cartId,
  });
}

export async function setBillingAddress(
  data: Omit<Excite.Commercetools.SetBillingAddressCommand, '$type'>,
  resolveCheckoutData = true,
  cartId?: string,
) {
  const { send } = await import('./command');
  return send({
    $type: 'SetBillingAddressCommand',
    ...data,
    resolveCheckoutData,
    cartId,
  });
}

export async function setShippingAddress(
  data: Omit<Excite.Commercetools.SetShippingAddressCommand, '$type'>,
  resolveCheckoutData = true,
  cartId?: string,
) {
  const { send } = await import('./command');
  return send({
    $type: 'SetShippingAddressCommand',
    ...data,
    resolveCheckoutData,
    cartId,
  });
}

export async function setCustomerTypeOnCart(customerType: Excite.Commercetools.CustomerType, cartId?: string) {
  const { send } = await import('./command');
  return send({
    $type: 'SetCustomerTypeOnCartCommand',
    customerType,
    resolveCheckoutData: true,
    cartId,
  });
}

export async function setUseShippingAddress(useShippingAddress: boolean, resolveCheckoutData = true, cartId?: string) {
  const checkout = getDynamicData<Excite.Commercetools.CheckoutApiModel>(cartId ? { cartId } : undefined);
  checkout.update(current =>
    useShippingAddress
      ? {
          ...current,
          useShippingAddress,
          // Clear shipping address because billig address and shipping address
          // is cloned when alternative shipping address isn't perferred by the user
          shippingAddress: current.shippingAddress && {
            firstName: '',
            lastName: '',
            streetName: '',
            streetNumber: '',
            postalCode: '',
            city: '',
            region: '',
            state: '',
            additionalAddressInfo: '',
            viewData: current.shippingAddress.viewData,
          },
        }
      : { ...current, useShippingAddress },
  );

  const { send } = await import('./command');

  return send({
    $type: 'SetUseShippingAddressCommand',
    useShippingAddress,
    resolveCheckoutData,
    cartId,
  });
}

export async function setShippingMethod(id: string, resolveCheckoutData = true, cartId?: string) {
  const checkout = getDynamicData<Excite.Commercetools.CheckoutApiModel>(cartId ? { cartId } : undefined);
  checkout.update(current => ({ ...current, selectedShippingMethodId: id }));

  const { send } = await import('./command');

  return send({
    $type: 'SetShippingMethodCommand',
    id,
    resolveCheckoutData,
    cartId,
  });
}

export type SetCustomShippingAddressData = {
  id: string;
  name: string;
  description: string;
  centAmount: number;
};

export async function setCustomShippingMethod(
  customShippingMethod: SetCustomShippingAddressData,
  resolveCheckoutData = true,
  cartId?: string,
) {
  const { send } = await import('./command');
  return send({
    $type: 'SetCustomShippingMethodCommand',
    ...customShippingMethod,
    resolveCheckoutData,
    cartId,
  });
}

export async function setCountry(countryCode: string, cartId?: string) {
  const { send } = await import('./command');
  return send({
    $type: 'SetCountryCommand',
    countryCode,
    resolveCheckoutData: true,
    cartId,
  });
}
